import { type RouteObject } from 'react-router-dom';

// ── Pages publiques ───────────────────────────────────────────
import HomePage          from '@/pages/home/page';
import NotFound          from '@/pages/NotFound';
import MobileMoney       from '@/pages/mobile-money/page';
import OfflineFeatures   from '@/pages/offline-features/page';
import GuestCheckout     from '@/pages/guest-checkout/page';
import TrackingPage      from '@/pages/tracking/page';

// ── Auth ──────────────────────────────────────────────────────
import RegisterPage      from '@/pages/auth/register/page';
import LoginPage         from '@/pages/auth/login/page';
import JoinPage          from '@/pages/auth/join/page';
import ForgotPasswordPage from '@/pages/auth/forgot-password/page';
import ResetPasswordPage  from '@/pages/auth/reset-password/page';
import VerifyEmailPage    from '@/pages/auth/verify-email/page';

// ── Back-office (nouveau template) ────────────────────────────
import {
  ClientDashboard,
  NewOrder,
  OrdersList,
  OrderDetails,
  ConfirmPayPage,
  LiveTracking,
  NeighborsPage,
  WalletPage,
  AddressesPage,
  ProfilePage,
  SettingsPage,
  NotificationsPage,
  HelpPage,
} from '@/backoffice/pages/client';
import {
  ProDriverDashboard,
  ProMissionsPage,
  ProEarningsPage,
  ProRoutePage,
  ProZonesPage,
  ProDocumentsPage,
  ProInsurancePage,
} from '@/backoffice/pages/pro-driver';
import {
  CommunityDriverDashboard,
  CommunityMissionsPage,
  CommunityTripsPage,
  CommunityEarningsPage,
} from '@/backoffice/pages/community-driver';
import {
  AdminDashboard,
  AdminUsersPage,
  AdminOrdersPage,
  AdminDisputesPage,
  AdminDocumentsPage,
  AdminInfrastructurePage,
  AdminPromotionsPage,
  AdminReportsPage,
  AdminSupportPage,
  AdminCommunicationPage,
  AdminGeographyPage,
} from '@/backoffice/pages/admin';
import {
  SuperAdminDashboard,
  SuperAdminsPage,
  SuperConfigPage,
  SuperAdminReportsPage,
  SuperAdminPricingPage,
  SuperAdminCommunicationPage,
} from '@/backoffice/pages/super-admin';

// ── Dashboard unifié entités (CompanyProfile) ─────────────────
import EntityDashboard from '@/pages/dashboards/entity-dashboard/page';
import { ENTITY_CONFIGS } from '@/domains/company/constants/entity.config';
import CompanyDashboard from '@/pages/auth/company-dashboard/page';

import {
  SharedProfilePage,
  SharedSettingsPage,
  SharedNotificationsPage,
  SharedHelpPage,
} from '@/backoffice/pages/shared';
import {
  RelayPointDashboardPage,
  RelayPointPackagesPage,
  RelayPointActivitiesPage,
} from '@/backoffice/pages/relay-point';

import PublicRoute from '@/components/PublicRoute';
import ProtectedRoute from '@/components/ProtectedRoute';

const routes: RouteObject[] = [

  // ── Pages publiques ────────────────────────────────────────
  { path: '/',                 element: <HomePage /> },
  { path: '/mobile-money',     element: <MobileMoney /> },
  { path: '/offline-features', element: <OfflineFeatures /> },
  { path: '/guest-checkout',   element: <GuestCheckout /> },
  { path: '/tracking',         element: <TrackingPage /> },

  // ── AUTH ───────────────────────────────────────────────────
  {
    element: <PublicRoute />,
    children: [
      { path: '/register/:userType', element: <RegisterPage /> },
      { path: '/login',              element: <LoginPage /> },
      { path: '/forgot-password',    element: <ForgotPasswordPage /> },
      { path: '/reset-password',     element: <ResetPasswordPage /> },
      { path: '/join/:token',        element: <JoinPage /> },
    ],
  },

  { path: '/verify-email', element: <VerifyEmailPage /> },

  // ── DASHBOARDS PROTÉGÉS ────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [

      // ── Client ────────────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['client']} />,
        children: [
          { path: '/dashboard/client',                      element: <ClientDashboard /> },
          { path: '/dashboard/client/commandes',            element: <OrdersList /> },
          { path: '/dashboard/client/commandes/nouvelle',   element: <NewOrder /> },
          { path: '/dashboard/client/commandes/:id',        element: <OrderDetails /> },
          { path: '/dashboard/client/commandes/:id/paiement', element: <ConfirmPayPage /> },
          { path: '/dashboard/client/suivi',                element: <LiveTracking /> },
          { path: '/dashboard/client/adresses',             element: <AddressesPage /> },
          { path: '/dashboard/client/voisins',              element: <NeighborsPage /> },
          { path: '/dashboard/client/portefeuille',         element: <WalletPage /> },
          { path: '/dashboard/client/historique',           element: <OrdersList /> },
          { path: '/dashboard/client/profil',              element: <ProfilePage /> },
          { path: '/dashboard/client/parametres',          element: <SettingsPage /> },
          { path: '/dashboard/client/notifications',       element: <NotificationsPage /> },
          { path: '/dashboard/client/aide',                element: <HelpPage /> },
        ],
      },

      // ── Livreur communautaire ─────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['community_delivery']} />,
        children: [
          { path: '/dashboard/livreur',            element: <CommunityDriverDashboard /> },
          { path: '/dashboard/livreur/missions',   element: <CommunityMissionsPage /> },
          { path: '/dashboard/livreur/trajets',    element: <CommunityTripsPage /> },
          { path: '/dashboard/livreur/gains',      element: <CommunityEarningsPage /> },
          { path: '/dashboard/livreur/infos',      element: <CommunityDriverDashboard /> },
          { path: '/dashboard/livreur/profil',         element: <SharedProfilePage /> },
          { path: '/dashboard/livreur/parametres',     element: <SharedSettingsPage /> },
          { path: '/dashboard/livreur/notifications',  element: <SharedNotificationsPage /> },
          { path: '/dashboard/livreur/aide',           element: <SharedHelpPage /> },
        ],
      },

      // ── Livreur professionnel ─────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['professional_delivery']} />,
        children: [
          { path: '/dashboard/livreur-pro',              element: <ProDriverDashboard /> },
          { path: '/dashboard/livreur-pro/missions',     element: <ProMissionsPage /> },
          { path: '/dashboard/livreur-pro/itineraire',   element: <ProRoutePage /> },
          { path: '/dashboard/livreur-pro/revenus',      element: <ProEarningsPage /> },
          { path: '/dashboard/livreur-pro/zones',        element: <ProZonesPage /> },
          { path: '/dashboard/livreur-pro/documents',    element: <ProDocumentsPage /> },
          { path: '/dashboard/livreur-pro/assurance',    element: <ProInsurancePage /> },
          { path: '/dashboard/livreur-pro/profil',         element: <SharedProfilePage /> },
          { path: '/dashboard/livreur-pro/parametres',     element: <SharedSettingsPage /> },
          { path: '/dashboard/livreur-pro/notifications',  element: <SharedNotificationsPage /> },
          { path: '/dashboard/livreur-pro/aide',           element: <SharedHelpPage /> },
        ],
      },

      // ── Entités company ───────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['company']} />,
        children: [
          { path: '/dashboard/company', element: <CompanyDashboard /> },
        ],
      },
      {
        element: <ProtectedRoute allowedRoles={['company']} />,
        children: Object.values(ENTITY_CONFIGS).map((cfg) => ({
          path: cfg.routePath,
          element: <EntityDashboard />,
        })),
      },

      // ── Point relais ──────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['relay_point']} />,
        children: [
          { path: '/dashboard/point-relais',             element: <RelayPointDashboardPage /> },
          { path: '/dashboard/point-relais/colis',      element: <RelayPointPackagesPage /> },
          { path: '/dashboard/point-relais/activites',  element: <RelayPointActivitiesPage /> },
          { path: '/dashboard/point-relais/profil',         element: <SharedProfilePage /> },
          { path: '/dashboard/point-relais/parametres',     element: <SharedSettingsPage /> },
          { path: '/dashboard/point-relais/notifications',  element: <SharedNotificationsPage /> },
          { path: '/dashboard/point-relais/aide',           element: <SharedHelpPage /> },
        ],
      },

      // ── Admin ─────────────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['admin']} />,
        children: [
          { path: '/dashboard/admin',                    element: <AdminDashboard /> },
          { path: '/dashboard/admin/utilisateurs',       element: <AdminUsersPage /> },
          { path: '/dashboard/admin/commandes',          element: <AdminOrdersPage /> },
          { path: '/dashboard/admin/litiges',            element: <AdminDisputesPage /> },
          { path: '/dashboard/admin/documents',          element: <AdminDocumentsPage /> },
          { path: '/dashboard/admin/configuration',    element: <SuperConfigPage /> },
          { path: '/dashboard/admin/infrastructures',    element: <AdminInfrastructurePage /> },
          { path: '/dashboard/admin/geographie',        element: <AdminGeographyPage /> },
          { path: '/dashboard/admin/promotions',         element: <AdminPromotionsPage /> },
          { path: '/dashboard/admin/support',           element: <AdminSupportPage /> },
          { path: '/dashboard/admin/communication',    element: <AdminCommunicationPage /> },
          { path: '/dashboard/admin/rapports',          element: <AdminReportsPage /> },
          { path: '/dashboard/admin/profil',            element: <SharedProfilePage /> },
          { path: '/dashboard/admin/parametres',        element: <SharedSettingsPage /> },
          { path: '/dashboard/admin/notifications',     element: <SharedNotificationsPage /> },
          { path: '/dashboard/admin/aide',              element: <SharedHelpPage /> },
        ],
      },

      // ── Super-admin ───────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['super_admin']} />,
        children: [
          { path: '/dashboard/super-admin',                      element: <SuperAdminDashboard /> },
          { path: '/dashboard/super-admin/administrateurs',      element: <SuperAdminsPage /> },
          { path: '/dashboard/super-admin/utilisateurs',         element: <AdminUsersPage /> },
          { path: '/dashboard/super-admin/configuration',        element: <SuperConfigPage /> },
          { path: '/dashboard/super-admin/zones',                element: <AdminInfrastructurePage /> },
          { path: '/dashboard/super-admin/geographie',         element: <AdminGeographyPage /> },
          { path: '/dashboard/super-admin/tarification',         element: <SuperAdminPricingPage /> },
          { path: '/dashboard/super-admin/rapports',             element: <SuperAdminReportsPage /> },
          { path: '/dashboard/super-admin/communication',        element: <SuperAdminCommunicationPage /> },
          { path: '/dashboard/super-admin/profil',               element: <SharedProfilePage /> },
          { path: '/dashboard/super-admin/parametres',           element: <SharedSettingsPage /> },
          { path: '/dashboard/super-admin/notifications',        element: <SharedNotificationsPage /> },
          { path: '/dashboard/super-admin/aide',                   element: <SharedHelpPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFound /> },
];

export default routes;
